import React, { useMemo, useState } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  makeStyles
} from '@material-ui/core'
import { CheckCircle, ErrorOutline } from '@material-ui/icons'
import { CommissionCase } from '../types'
import { money } from '../utils'
import ConceptDisplay from './ConceptDisplay'

const useStyles = makeStyles({
  tableContainer: {
    border: '1px solid #e1e4e8',
    borderRadius: 6,
    boxShadow: '0 2px 8px rgba(31, 38, 46, 0.05)',
    '& .MuiTableCell-head': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      background: '#f56600',
      color: '#fff',
      fontWeight: 700,
      borderBottom: '1px solid #d95b00',
      '& .MuiTableSortLabel-root': { color: '#fff' },
      '& .MuiTableSortLabel-root:hover': { color: '#fff' },
      '& .MuiTableSortLabel-root.MuiTableSortLabel-active': { color: '#fff' },
      '& .MuiTableSortLabel-icon': { color: '#fff !important' }
    },
    '& .MuiTableRow-root': { transition: 'background-color 120ms ease' },
    '& .MuiTableRow-root:hover': { backgroundColor: '#fafbfc' },
    '& .MuiTableCell-body': { borderBottomColor: '#eceef1', paddingTop: 9, paddingBottom: 9 }
  },
  table: { minWidth: 1120 },
  saverName: { fontWeight: 600, color: '#282d33' },
  secondaryText: { color: '#6f7781', fontVariantNumeric: 'tabular-nums' },
  moneyCell: { fontWeight: 600, color: '#343a42', fontVariantNumeric: 'tabular-nums' },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    borderRadius: 999,
    padding: '5px 9px',
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    '& svg': { fontSize: 16 }
  },
  statusPaid: { color: '#237a3b', background: '#eaf6ed' },
  statusPartial: { color: '#9a4a0a', background: '#fff2e4' },
  statusPending: { color: '#626a74', background: '#eceef1' }
})

interface Props {
  admin: boolean
  rows: CommissionCase[]
  selected: Set<string>
  onToggle: (key: string) => void
}

type SortColumn = 'name' | 'office' | 'success'

const CommissionTable: React.FC<Props> = ({ admin, rows, selected, onToggle }) => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const [sortBy, setSortBy] = useState<SortColumn>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const rowsPerPage = 10
  const sortedRows = useMemo(() => {
    const value = (row: CommissionCase) => {
      if (sortBy === 'office') return row.stages.office || ''
      if (sortBy === 'success') return row.stages.success || ''
      return row.name || ''
    }
    return [...rows].sort((left, right) => {
      const result = value(left).localeCompare(value(right), 'es', { sensitivity: 'base', numeric: true })
      return sortDirection === 'asc' ? result : -result
    })
  }, [rows, sortBy, sortDirection])
  const changeSort = (column: SortColumn) => {
    if (sortBy === column) setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'))
    else {
      setSortBy(column)
      setSortDirection('asc')
    }
    setPage(0)
  }
  const sortHeader = (column: SortColumn, label: string) => (
    <TableSortLabel
      active={sortBy === column}
      direction={sortBy === column ? sortDirection : 'asc'}
      onClick={() => changeSort(column)}
    >
      {label}
    </TableSortLabel>
  )
  return (
    <TableContainer className={classes.tableContainer} component={Paper}>
      <Table className={classes.table} size="small">
        <TableHead>
          <TableRow>
            <TableCell>{sortHeader('name', 'Ahorrador')}</TableCell>
            <TableCell>{sortHeader('office', 'Oficina')}</TableCell>
            <TableCell>{sortHeader('success', 'Éxito')}</TableCell>
            <TableCell align="right">Honorarios</TableCell>
            <TableCell>Comisión</TableCell>
            <TableCell>Estado</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
            <TableRow key={row.id}>
              <TableCell>
                <span className={classes.saverName}>{row.name}</span>
              </TableCell>
              <TableCell>
                <span className={classes.secondaryText}>{row.stages.office || '-'}</span>
              </TableCell>
              <TableCell>
                <span className={classes.secondaryText}>{row.stages.success || '-'}</span>
              </TableCell>
              <TableCell align="right">
                <span className={classes.moneyCell}>{money(row.stages.fee || 0)}</span>
              </TableCell>
              <TableCell>
                <ConceptDisplay row={row} admin={admin} selected={selected} onToggle={onToggle} />
              </TableCell>
              <TableCell>
                <span
                  className={`${classes.statusPill} ${
                    row.paymentState === 'paid'
                      ? classes.statusPaid
                      : row.paymentState === 'partial'
                      ? classes.statusPartial
                      : classes.statusPending
                  }`}
                >
                  {row.paymentState === 'paid' ? <CheckCircle /> : <ErrorOutline />}
                  {row.paymentState === 'paid' ? 'Pagada' : row.paymentState === 'partial' ? 'Parcial' : 'Pendiente'}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
        onChangePage={(_event, value) => setPage(value)}
      />
    </TableContainer>
  )
}

export default CommissionTable
